import React, { CSSProperties, ReactNode, useState } from 'react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import styles from './NewContentDialog.module.css';
import { useCreateContentMutation, useGetApplicationNameListQuery, useGetCategoryListQuery, useUploadContentMutation } from '../../../generated/graphql';
import { FormAutoComplete, FormField, FormFileUpload, FormTextArea } from '../../form';

const FALLBACK_CATEGORIES = ['Workset', 'Family', 'Template', 'Detail Component', 'Annotation', 'Title Block'];
const FALLBACK_APPLICATION_NAMES = ['Revit', 'AutoCAD', 'Civil 3D', 'Navisworks', 'ArchiCAD', 'Tekla', 'IFC'];

async function uploadToSas(sasUrl: string, file: File): Promise<void> {
    const response = await fetch(sasUrl, {
        method: 'PUT',
        headers: { 'x-ms-blob-type': 'BlockBlob' },
        body: file,
    });
    if (!response.ok) throw new Error(`Upload failed (${response.status}).`);
}

type NewContentDialogProps = {
    onClose: () => void;
    libraryId: string;
    visible: boolean;
    style?: CSSProperties;
    children?: ReactNode;
};

const NewContentDialog = ({ style, visible, libraryId, onClose }: NewContentDialogProps) => {
    const [name, setName] = useState('');
    const [category, setCategory] = useState('');
    const [description, setDescription] = useState('');
    const [applicationName, setApplicationName] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [thumbnail, setThumbnail] = useState<File | null>(null);
    const [validationError, setValidationError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [createContent] = useCreateContentMutation();
    const [uploadContent] = useUploadContentMutation();
    const categoryList = useGetCategoryListQuery();
    const applicationNameList = useGetApplicationNameListQuery();

    const loadedCategories = categoryList.data?.CategoryList?.filter((value): value is string => !!value) || [];
    const loadedApplicationNames = applicationNameList.data?.ApplicationNameList?.filter((value): value is string => !!value) || [];
    const categories = loadedCategories.length > 0 ? loadedCategories : FALLBACK_CATEGORIES;
    const applicationNames = loadedApplicationNames.length > 0 ? loadedApplicationNames : FALLBACK_APPLICATION_NAMES;

    const handleSubmit = async () => {
        if (!name.trim() || !category.trim() || !description.trim()) {
            setValidationError('Please fill in Name, Category, and Description.');
            return;
        }
        if (file && !applicationName.trim()) {
            setValidationError('Application Name is required when uploading a file.');
            return;
        }
        if (thumbnail && !file) {
            setValidationError('Please select a content file before adding a thumbnail.');
            return;
        }

        setValidationError('');
        setIsSubmitting(true);
        try {
            if (!file) {
                await createContent({
                    variables: {
                        content: {
                            name: name.trim(),
                            category: category.trim(),
                            description: description.trim(),
                            libraryId,
                        },
                    },
                });
            } else {
                const result = await uploadContent({
                    variables: {
                        libraryId,
                        version: {
                            name: name.trim(),
                            category: category.trim(),
                            description: description.trim(),
                            applicationName: applicationName.trim(),
                            attachment: { name: file.name },
                            thumbnail: thumbnail ? { name: thumbnail.name } : undefined,
                        },
                        fileName: file.name,
                        thumbnailName: thumbnail?.name ?? null,
                        tagIds: [],
                        overwrite: false,
                    },
                });

                const uploadedContent = result.data?.Library?.uploadContent;
                if (!uploadedContent?.setFile) throw new Error('The server did not return a file upload URL.');
                await uploadToSas(uploadedContent.setFile, file);

                if (thumbnail) {
                    if (!uploadedContent.setThumbnail) throw new Error('The server did not return a thumbnail upload URL.');
                    await uploadToSas(uploadedContent.setThumbnail, thumbnail);
                }
            }
            onClose();
        } catch (error) {
            setValidationError(error instanceof Error ? error.message : 'Unable to create the content. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <DialogBase
            title="New Content"
            type="OkCancel"
            visible={visible}
            onClose={onClose}
            onSubmit={handleSubmit}
            validationError={validationError}
            className={styles.NewContentDialog}
            style={style}
            data-testid="NewContentDialog"
            isLoading={isSubmitting}
            width="640px"
        >
            <FormField label="Name" name="name" value={name} onChange={setName} placeholder="Enter content name" required />
            <FormAutoComplete label="Category" name="category" value={category} onChange={setCategory} options={categories} placeholder="Select or type a category" menuFixed />
            <FormTextArea label="Description" name="description" value={description} onChange={setDescription} />

            <div className="my-4 border-t" style={{ borderColor: 'var(--border-default)' }} />
            <div className="mb-3">
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>File and preview</h3>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>Optional. Add a file to create a file-backed content version.</p>
            </div>
            <FormFileUpload label="Content file (optional)" name="file" file={file} onChange={(selected) => { setFile(selected); if (!selected) setThumbnail(null); }} />
            {file && (
                <>
                    <FormAutoComplete label="Application Name" name="applicationName" value={applicationName} onChange={setApplicationName} options={applicationNames} placeholder="Select or type an application" menuFixed />
                    <FormFileUpload label="Thumbnail (optional)" name="thumbnail" file={thumbnail} onChange={setThumbnail} accept="image/*" />
                </>
            )}
        </DialogBase>
    );
};

export default NewContentDialog;
